import { ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Caption, SubHeading, ThemedText } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import type { TripSummaryMoment } from '@/services/tripSummary';
import type { ItineraryItem } from '@/services/itinerary';

type TripSummaryTopMomentsProps = {
  moments: TripSummaryMoment[];
};

const getCategoryIcon = (category: ItineraryItem['category']): keyof typeof Ionicons.glyphMap => {
  switch (category) {
    case 'flight':
      return 'airplane-outline';
    case 'hotel':
      return 'bed-outline';
    case 'activity':
      return 'ticket-outline';
    case 'ferry':
      return 'boat-outline';
    case 'train':
      return 'train-outline';
    case 'transportation':
      return 'bus-outline';
    default:
      return 'location-outline';
  }
};

export function TripSummaryTopMoments({ moments }: TripSummaryTopMomentsProps) {
  const theme = useTheme();

  if (moments.length < 2) {
    return null;
  }

  return (
    <View style={{ gap: theme.spacing(0.85) }}>
      <SubHeading style={{ fontSize: 18 }}>Top moments</SubHeading>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ gap: theme.spacing(0.85), paddingRight: theme.spacing(0.5) }}
      >
        {moments.map((moment) => (
          <View
            key={moment.id}
            style={{
              width: 168,
              padding: theme.spacing(1),
              borderRadius: theme.radii.lg,
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
              backgroundColor: theme.colors.backgroundSecondary,
              gap: theme.spacing(0.5),
            }}
          >
            <View
              style={{
                width: 30,
                height: 30,
                borderRadius: theme.radii.pill,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.colors.primary + '18',
              }}
            >
              <Ionicons name={getCategoryIcon(moment.category)} size={16} color={theme.colors.primary} />
            </View>
            <ThemedText numberOfLines={2} variant="bodyStrong" style={{ fontSize: 14, lineHeight: 18 }}>
              {moment.title}
            </ThemedText>
            {moment.location ? (
              <Caption numberOfLines={1} color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                {moment.location}
              </Caption>
            ) : null}
            <Caption
              style={{
                alignSelf: 'flex-start',
                paddingHorizontal: theme.spacing(0.7),
                paddingVertical: theme.spacing(0.25),
                borderRadius: theme.radii.pill,
                backgroundColor: theme.colors.surface,
                fontSize: 10,
                fontWeight: '700',
              }}
            >
              {moment.dateLabel}
            </Caption>
          </View>
        ))}
      </ScrollView>
    </View>
  );
}
